import { BehaviorSubject, map } from 'rxjs';
import { camelCaseToWords } from '@core/shared/utils';
import * as deepmerge from "deepmerge";
import { distinctUntilChangedDeep } from '@core/shared/utils';
import { isEqualDeep } from '@core/shared/utils';
import { PersistStateFactory } from '../utils';
import * as i0 from "@angular/core";
import * as i1 from "../utils";
const DEV_SETTINGS_LOCALSTORAGE_KEY = 'DEV_SETTINGS';
export let DevToolsRegistry = /*#__PURE__*/(() => {
  class DevToolsRegistry {
    constructor(persistenceFactory) {
      this.persistenceFactory = persistenceFactory;
      this.debugFunctionRegistry = new Map();
      this.devSettingsRegistry = new Map();
      this.devSettingsState = new BehaviorSubject({});
      this.persistedDevSettings = this.persistenceFactory.create(DEV_SETTINGS_LOCALSTORAGE_KEY, false, 'local');
      if (Object.keys(this.persistedDevSettings.get({})).length > 0) {
        console.warn('Loaded persisted dev settings:', this.persistedDevSettings);
      }
    }
    /******* DEBUG FUNCTIONS ************************************/
    getDebugFunctionNames() {
      const items = Array.from(this.debugFunctionRegistry.keys());
      items.sort((a, b) => a < b ? -1 : 1);
      return items;
    }
    getDebugFunctions() {
      const items = Array.from(this.debugFunctionRegistry.values()).map(regEntry => regEntry.settings);
      items.sort((a, b) => a.displayName < b.displayName ? -1 : 1);
      return items;
    }
    executeDebugFunction(name) {
      return this.debugFunctionRegistry.get(name).func();
    }
    exposeDebugFunction(name, arg2, arg3) {
      const namePath = Array.isArray(name) ? name : [name];
      const id = namePath.join('/');
      let settings = {
        id: id,
        name: namePath
      };
      let func;
      if (arg3) {
        func = arg3;
        settings = {
          ...settings,
          ...arg2
        };
      } else {
        func = arg2;
      }
      this.debugFunctionRegistry.set(id, {
        settings,
        func
      });
    }
    /******* DEV SETTINGS ************************************/
    getDevSettings() {
      const items = Array.from(this.devSettingsRegistry.values());
      items.sort((a, b) => a.displayName < b.displayName ? -1 : 1);
      return items;
    }
    getCurrentDevSettingsState() {
      return {
        ...this.devSettingsState.value
      };
    }
    defineDevSetting(setting) {
      if (!setting.displayName) setting.displayName = camelCaseToWords(setting.id);
      this.devSettingsRegistry.set(setting.id, setting);
      const value = setting.persist ? this.persistedDevSettings.get()?.[setting.id] || setting.defaultValue : setting.defaultValue;
      this.updateDevSettingsState({
        [setting.id]: value
      });
    }
    resetDevSettings(id) {
      if (id) {
        const setting = this.devSettingsRegistry.get(id);
        this.updateDevSettingsState({
          [setting.id]: setting.defaultValue
        });
      } else {
        // reset all
        const settings = Array.from(this.devSettingsRegistry.values());
        const defaultSettingsMap = settings.reduce((data, setting) => {
          data[setting.id] = setting.defaultValue;
          return data;
        }, {});
        this.updateDevSettingsState(defaultSettingsMap);
      }
    }
    // generate a Selector-style observable that emits whenever one of the passed IDs changes
    subscribeToDevSettings(ids) {
      return this.devSettingsState.pipe(map(state => {
        return ids.reduce((data, id) => {
          data[id] = state[id] || null;
          return data;
        }, {});
      }), distinctUntilChangedDeep());
    }
    updateDevSettingsState(changes) {
      const newState = deepmerge.all([{}, this.devSettingsState.value, changes]);
      // before any further actions, check if something has actually changed
      if (!isEqualDeep(this.devSettingsState.value, newState)) {
        this.devSettingsState.next(newState);
        this.notifyChangedSettings(changes);
        this.persistSettings(changes);
      }
    }
    notifyChangedSettings(changes) {
      const settings = Array.from(this.devSettingsRegistry.values());
      const changedIDs = Object.keys(changes);
      const toBeNotifiedSettings = settings.filter(s => {
        return s.onChange && changedIDs.includes(s.id);
      });
      toBeNotifiedSettings.forEach(setting => {
        const newValue = changes[setting.id];
        setting.onChange(newValue);
      });
    }
    persistSettings(changes) {
      const settings = Array.from(this.devSettingsRegistry.values());
      const state = this.devSettingsState.value;
      const persistData = settings.filter(s => s.persist).reduce((data, setting) => {
        data[setting.id] = state[setting.id];
        return data;
      }, {});
      this.persistedDevSettings.set(persistData);
    }
    static {
      this.ɵfac = function DevToolsRegistry_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DevToolsRegistry)(i0.ɵɵinject(i1.PersistStateFactory));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: DevToolsRegistry,
        factory: DevToolsRegistry.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DevToolsRegistry;
})();